import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { isVersionConflict } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { formatDateTime } from "../lib/missions";
import {
  FILE_KINDS, MISSING_LABELS, RUN_STATUS_LABELS, answerPayload, followUpDraft, isVisible, procedureErrorMessage, sectionProgress, valueLabel,
} from "../lib/procedures";
import ChecklistItemField from "../components/procedures/ChecklistItemField";
import TaskFormSheet from "../components/TaskFormSheet";
import { ReceiptPreviewDialog } from "../components/finance/ReceiptPreview";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Camera, Check, ChevronDown, ChevronLeft, ChevronRight, CircleAlert, Loader2, MessageSquare, Paperclip, RefreshCw, X,
} from "lucide-react";
import { useBottomBar } from "../hooks/useCopilotDock";

const DRAFT_KINDS = ["text", "number"]; // saved when the field is left, not on every key
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// A procedure carried out within a mission, phone first: one section at a time, every answer saved on its own (nothing is
// lost if the network drops between two), the anomalies and what is missing on top, « Terminer » at the bottom.
export default function ChecklistRun() {
  const { id: missionId, runId } = useParams();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [sectionIndex, setSectionIndex] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({}); // item id -> saving | saved | error
  const [missing, setMissing] = useState(null); // after a refused « Terminer »
  const [completing, setCompleting] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [followUp, setFollowUp] = useState(null); // {item, values}
  const [preview, setPreview] = useState(null);
  const runRef = useRef(null);
  // A full-screen entry on a phone: the Copilote's bubble steps aside; on a computer it moves above « Terminer ».
  useBottomBar(true, { hideOnMobile: true, raise: false });
  useBottomBar(Boolean(run?.capabilities?.complete));
  const queues = useRef({});
  const base = `/missions/${missionId}/checklists/${runId}`;

  const apply = (data) => { runRef.current = data; setRun(data); };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPhase("loading");
    try {
      const { data } = await api.get(base);
      apply(data);
      setPhase("ready");
    } catch (e) {
      if (e?.response?.status === 404) { setPhase("missing"); return; }
      setErrorText(procedureErrorMessage(e, "Impossible de charger la procédure."));
      setPhase("error");
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  // One save at a time per item, each built from the latest answer the server returned (its version).
  const save = (item, changes) => {
    const previous = queues.current[item.id] || Promise.resolve();
    const next = previous.then(async () => {
      setSaving((s) => ({ ...s, [item.id]: "saving" }));
      try {
        const { data } = await api.put(`${base}/answers/${item.id}`, answerPayload(runRef.current.answers[item.id], changes));
        apply(data);
        setSaving((s) => ({ ...s, [item.id]: "saved" }));
        setMissing((m) => (m ? data.missing : m));
        if ("value" in changes) setDrafts((d) => { const { [item.id]: _drop, ...rest } = d; return rest; });
      } catch (e) {
        setSaving((s) => ({ ...s, [item.id]: "error" }));
        toast.error(procedureErrorMessage(e));
        if (isVersionConflict(e) || e?.response?.status === 409) {
          setDrafts((d) => { const { [item.id]: _drop, ...rest } = d; return rest; });
          load({ silent: true });
        }
      }
    });
    queues.current[item.id] = next;
    return next;
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`${base}/complete`, { expected_version: runRef.current.version });
      apply(data);
      setMissing(null);
      toast.success("Procédure terminée");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "run_incomplete") {
        setMissing(detail.missing);
        goTo(detail.missing[0]?.item_id);
      } else {
        toast.error(procedureErrorMessage(e));
        if (detail?.code === "version_conflict") load({ silent: true });
      }
    } finally {
      setCompleting(false);
    }
  };

  const sections = run?.sections || [];
  const answers = run?.answers || {};
  const goTo = (itemId) => {
    const index = sections.findIndex((s) => s.items.some((i) => i.id === itemId));
    if (index < 0) return;
    setSectionIndex(index);
    setTimeout(() => document.getElementById(`run-item-${itemId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="run-unavailable">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Cette procédure n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to={`/missions/${missionId}`}>Retour à la mission</Link></Button>
          {phase === "error" && <Button variant="outline" onClick={() => load()}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>}
        </div>
      </Card>
    );
  }

  const caps = run.capabilities;
  const section = sections[Math.min(sectionIndex, sections.length - 1)];
  const total = run.progress;
  const canFollowUp = caps.follow_up && hasPermission(user, "tasks:create");

  return (
    <div className="space-y-4 pb-24" data-testid="run-page">
      <Link to={`/missions/${missionId}`} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {run.mission.reference} · {run.mission.pharmacy_name}
      </Link>
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span>Version {run.version_number}</span>
          <span className={`rounded-full px-2 py-0.5 font-medium ${run.status === "completed" ? "bg-emerald-50 text-emerald-800" : "bg-jamora-50 text-jamora"}`}
            data-testid="run-status">{RUN_STATUS_LABELS[run.status]}</span>
          <span>{run.required ? "obligatoire" : "facultative"}</span>
        </div>
        <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="run-title">{run.label}</h1>
        <ProgressBar answered={total.answered} total={total.total} />
      </div>

      {run.status === "completed" && (
        <Card className="p-4 text-sm text-slate-700 flex items-center justify-between gap-3 flex-wrap" data-testid="run-completed">
          <span>Terminée le {formatDateTime(run.completed_at, run.mission.timezone)} par {run.completed_by_name}.</span>
          {caps.reopen && <Button variant="outline" size="sm" onClick={() => setReopenOpen(true)} data-testid="run-reopen">Rouvrir</Button>}
        </Card>
      )}
      {!caps.answer && run.status !== "completed" && (
        <p className="text-sm text-slate-500" data-testid="run-read-only">
          {run.mission.status === "in_progress" ? "Lecture seule : seule l'équipe de terrain remplit cette procédure." : "Lecture seule : la mission n'est pas en cours."}
        </p>
      )}

      <TopNotices run={run} missing={missing} onGo={goTo} />

      <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)] items-start">
        <SectionNav sections={sections} answers={answers} current={sectionIndex} onSelect={setSectionIndex} />
        <div className="space-y-3 min-w-0">
          <h3 className="text-base font-semibold text-slate-900" data-testid="run-section-title">{section?.title}</h3>
          {section?.items.filter((item) => isVisible(item, answers)).map((item) => (
            <RunItem key={item.id} item={item} answer={answers[item.id]} run={run} draft={drafts[item.id]} status={saving[item.id]}
              missingReason={missing?.find((m) => m.item_id === item.id)?.reason}
              onDraft={(v) => setDrafts((d) => ({ ...d, [item.id]: v }))} onSave={(changes) => save(item, changes)}
              onFollowUp={canFollowUp ? () => setFollowUp({ item, values: followUpDraft(run, item, answers[item.id]) }) : null}
              onPreview={(f) => setPreview({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })} />
          ))}
          <div className="flex justify-between gap-2 pt-1">
            <Button variant="outline" disabled={sectionIndex === 0} onClick={() => setSectionIndex((i) => i - 1)} data-testid="run-section-prev">
              <ChevronLeft size={15} className="mr-1" />Précédente
            </Button>
            <Button variant="outline" disabled={sectionIndex >= sections.length - 1} onClick={() => setSectionIndex((i) => i + 1)} data-testid="run-section-next">
              Suivante<ChevronRight size={15} className="ml-1" />
            </Button>
          </div>
          <RunHistory run={run} />
        </div>
      </div>

      {caps.complete && (
        <div className="fixed inset-x-0 bottom-[4.75rem] lg:bottom-4 z-30 px-4 lg:left-64" data-testid="run-complete-bar">
          <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-3 shadow-lg flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600">{total.answered} / {total.total} élément{total.total > 1 ? "s" : ""}</span>
            <Button onClick={complete} disabled={completing}  data-testid="run-complete">
              {completing && <Loader2 size={14} className="mr-1.5 animate-spin" />}Terminer la procédure
            </Button>
          </div>
        </div>
      )}

      <ReopenDialog open={reopenOpen} onOpenChange={setReopenOpen} url={`${base}/reopen`} version={run.version}
        onDone={(data) => { apply(data); setReopenOpen(false); toast.success("Procédure rouverte"); }} onConflict={() => load({ silent: true })} />
      <TaskFormSheet open={!!followUp} onOpenChange={(open) => { if (!open) setFollowUp(null); }} task={null}
        canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false} directory={[]} initialValues={followUp?.values}
        onSaved={async (task) => {
          const { item } = followUp;
          setFollowUp(null);
          try {
            const { data } = await api.post(`${base}/answers/${item.id}/follow-up`, { task_id: task.id });
            apply(data);
            toast.success("Tâche de suivi reliée à l'anomalie");
          } catch (e) {
            toast.error(procedureErrorMessage(e, "La tâche est créée mais n'a pas pu être reliée à l'anomalie."));
          }
        }} />
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Preuve jointe à la procédure." />
    </div>
  );
}

function ProgressBar({ answered, total }) {
  const pct = total ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid="run-progress">
      <div className="h-2 flex-1 max-w-sm rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full bg-jamora" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500">{answered} / {total}</span>
    </div>
  );
}

function TopNotices({ run, missing, onGo }) {
  return (
    <>
      {run.anomalies.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 space-y-1.5" data-testid="run-anomalies">
          <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5"><AlertTriangle size={15} />{run.anomalies.length} anomalie{run.anomalies.length > 1 ? "s" : ""}</p>
          <ul className="space-y-1">
            {run.anomalies.map((a) => {
              const item = run.sections.flatMap((s) => s.items).find((i) => i.id === a.item_id);
              return (
                <li key={a.item_id}>
                  <button type="button" onClick={() => onGo(a.item_id)} className="text-sm text-amber-900 underline-offset-2 hover:underline text-left">
                    {a.label}{a.value !== null && a.value !== undefined ? ` : ${valueLabel(item, a.value)}` : ""}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      {missing && missing.length > 0 && (
        <Card className="p-4 border-red-200 bg-red-50 space-y-1.5" role="alert" data-testid="run-missing">
          <p className="text-sm font-medium text-red-900">Pour terminer, complétez :</p>
          <ul className="space-y-1">
            {missing.map((m) => (
              <li key={m.item_id}>
                <button type="button" onClick={() => onGo(m.item_id)} className="text-sm text-red-900 underline-offset-2 hover:underline text-left">{m.label} : {MISSING_LABELS[m.reason]}</button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function SectionNav({ sections, answers, current, onSelect }) {
  return (
    <nav aria-label="Sections" className="lg:sticky lg:top-24">
      <ol className="flex lg:flex-col gap-2 overflow-x-auto pb-1 -mx-4 px-4 lg:mx-0 lg:px-0" data-testid="run-sections">
        {sections.map((s, index) => {
          const p = sectionProgress(s, answers);
          const active = index === current;
          const done = p.total > 0 && p.answered === p.total;
          return (
            <li key={s.id} className="shrink-0">
              <button type="button" onClick={() => onSelect(index)} aria-current={active ? "step" : undefined} data-testid={`run-section-${index}`}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${active ? "border-jamora bg-jamora-50 text-jamora" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                <span className="font-medium block truncate max-w-[12rem]">{s.title}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  {done && <Check size={12} className="text-emerald-600" />}{p.answered}/{p.total}
                  {p.anomalies > 0 && <span className="text-amber-700"> · {p.anomalies} anomalie{p.anomalies > 1 ? "s" : ""}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SaveState({ status }) {
  if (status === "saving") return <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Enregistrement…</span>;
  if (status === "saved") return <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="run-item-saved"><Check size={12} />Enregistré</span>;
  if (status === "error") return <span className="text-xs text-red-700 inline-flex items-center gap-1"><CircleAlert size={12} />Non enregistré</span>;
  return null;
}

function RunItem({ item, answer, run, draft, status, missingReason, onDraft, onSave, onFollowUp, onPreview }) {
  const [detailsOpen, setDetailsOpen] = useState(!!answer?.comment);
  const [comment, setComment] = useState(answer?.comment || "");
  const editable = run.capabilities.answer;
  useEffect(() => { setComment(answer?.comment || ""); }, [answer?.comment]);

  const value = draft !== undefined ? draft : answer?.value ?? null;
  const change = (v) => (DRAFT_KINDS.includes(item.kind) ? onDraft(v) : onSave({ value: v }));
  const blur = () => {
    if (draft === undefined) return;
    const saved = answer?.value ?? null;
    if (String(draft ?? "") !== String(saved ?? "")) onSave({ value: draft });
    else onDraft(undefined);
  };
  const performer = answer?.performed_by;
  const showFiles = FILE_KINDS.includes(item.kind) || item.proof !== "none";

  return (
    <Card id={`run-item-${item.id}`} className={`p-4 space-y-3 ${missingReason ? "border-red-300" : ""}`} data-testid={`run-item-${item.id}`}>
      <ChecklistItemField item={item} value={value} onChange={change} onBlur={blur} disabled={!editable} testid={`run-field-${item.id}`}
        fileSlot={showFiles ? <FileSlot item={item} answer={answer} editable={editable} onSave={onSave} onPreview={onPreview} /> : null} />
      {missingReason && <p className="text-sm text-red-700" data-testid={`run-item-missing-${item.id}`}>{MISSING_LABELS[missingReason]}</p>}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-500 min-w-0">
          {answer && (answer.value !== null || answer.files.length > 0) ? (
            <span>Réalisé par {performer?.name}{answer.recorded_by_name !== performer?.name ? ` · saisi par ${answer.recorded_by_name}` : ""} · {formatDateTime(answer.recorded_at, run.mission.timezone)}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <SaveState status={status} />
          {(editable || answer?.comment) && (
            <button type="button" onClick={() => setDetailsOpen((v) => !v)} aria-expanded={detailsOpen} data-testid={`run-item-details-${item.id}`}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <MessageSquare size={13} />Commentaire{editable && run.performers.length > 1 ? " · réalisé par" : ""}<ChevronDown size={12} className={detailsOpen ? "rotate-180" : ""} />
            </button>
          )}
        </div>
      </div>

      {detailsOpen && (
        <div className="space-y-3 border-t border-slate-100 pt-3">
          <div className="space-y-1.5">
            <Label htmlFor={`comment-${item.id}`} className="text-xs">Commentaire</Label>
            <Textarea id={`comment-${item.id}`} rows={2} maxLength={1000} className="text-base md:text-sm" value={comment} disabled={!editable}
              onChange={(e) => setComment(e.target.value)} data-testid={`run-item-comment-${item.id}`}
              onBlur={() => { if ((comment.trim() || null) !== (answer?.comment || null)) onSave({ comment: comment.trim() || null }); }} />
          </div>
          {editable && run.performers.length > 1 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Réalisé par</Label>
              <Select value={performer ? `${performer.kind}:${performer.id}` : ""}
                onValueChange={(v) => { const [kind, id] = v.split(":"); onSave({ performed_by: { kind, id } }); }}>
                <SelectTrigger className="max-w-xs" data-testid={`run-item-performer-${item.id}`}><SelectValue placeholder="Moi" /></SelectTrigger>
                <SelectContent>
                  {run.performers.map((p) => <SelectItem key={`${p.kind}:${p.id}`} value={`${p.kind}:${p.id}`}>{p.name}{p.kind === "collaborator" ? " (sans compte)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {answer?.anomaly && (answer.follow_up_task ? (
        <p className="text-sm text-slate-700" data-testid={`run-item-follow-up-${item.id}`}>
          Tâche de suivi : {answer.follow_up_task.visible ? answer.follow_up_task.title : "créée"}
        </p>
      ) : onFollowUp && (
        <Button variant="outline" size="sm" onClick={onFollowUp} data-testid={`run-item-follow-up-create-${item.id}`}>Créer une tâche de suivi</Button>
      ))}
    </Card>
  );
}

// The answer's files (a photo/file item) or its proof: send one or several, open them, remove one (it is kept but stops counting).
function FileSlot({ item, answer, editable, onSave, onPreview }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = answer?.files || [];
  const photo = item.kind === "photo" || (!FILE_KINDS.includes(item.kind) && item.proof === "photo");

  const upload = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    const ids = [];
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files", form, { params: { context: "checklist_answer" }, headers: { "Idempotency-Key": newKey() } });
        ids.push(data.id);
      }
    } catch (e) {
      toast.error(procedureErrorMessage(e, "Fichier refusé : PDF ou image (JPEG, PNG, WebP) de 10 Mo maximum."));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
    if (ids.length) await onSave({ file_ids: [...files.map((f) => f.id), ...ids] });
  };

  return (
    <div className="space-y-2">
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid={`run-files-${item.id}`}>
          {files.map((f) => (
            <li key={f.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 pl-2 text-sm">
              <button type="button" onClick={() => onPreview(f)} className="max-w-[12rem] truncate py-1 hover:underline">{f.name}</button>
              {editable && (
                <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => onSave({ file_ids: files.filter((x) => x.id !== f.id).map((x) => x.id) })}
                  className="p-1.5 text-slate-500 hover:text-red-700"><X size={13} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <>
          <input ref={input} type="file" className="hidden" multiple accept={photo ? "image/jpeg,image/png,image/webp" : "image/jpeg,image/png,image/webp,application/pdf"}
            {...(photo ? { capture: "environment" } : {})} onChange={(e) => upload(Array.from(e.target.files || []))} data-testid={`run-file-input-${item.id}`} />
          <Button type="button" variant="outline" className="h-11" disabled={uploading} onClick={() => input.current?.click()} data-testid={`run-file-add-${item.id}`}>
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : photo ? <Camera size={16} className="mr-2" /> : <Paperclip size={16} className="mr-2" />}
            {photo ? "Prendre ou joindre une photo" : "Joindre un fichier"}
          </Button>
          <p className="text-xs text-slate-500">Évitez les visages et les données des clients sur les photos.</p>
        </>
      )}
    </div>
  );
}

const HISTORY_TEXT = { started: "Procédure démarrée", answered: "Réponse enregistrée", completed: "Procédure terminée", reopened: "Procédure rouverte",
  follow_up_linked: "Tâche de suivi reliée" };

function RunHistory({ run }) {
  const [open, setOpen] = useState(false);
  const labels = useMemo(() => Object.fromEntries(run.sections.flatMap((s) => s.items).map((i) => [i.id, i.label])), [run.sections]);
  return (
    <Card className="p-4">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="text-sm font-medium text-slate-900 inline-flex items-center gap-1"
        data-testid="run-history-toggle">
        <ChevronDown size={14} className={open ? "rotate-180" : ""} />Historique ({run.history.length})
      </button>
      {open && (
        <ol className="space-y-2 pt-3" data-testid="run-history">
          {run.history.map((h) => (
            <li key={h.id} className="text-sm">
              <div className="text-slate-800">{HISTORY_TEXT[h.kind] || h.kind}{h.data?.item_id ? ` : ${labels[h.data.item_id] || ""}` : ""}{h.data?.anomaly ? " (anomalie)" : ""}</div>
              <div className="text-xs text-slate-500">{formatDateTime(h.at, run.mission.timezone)} · {h.actor_name}</div>
              {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function ReopenDialog({ open, onOpenChange, url, version, onDone, onConflict }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setReason(""); setError(""); } }, [open]);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(url, { expected_version: version, reason: reason.trim() });
      onDone(data);
    } catch (e) {
      setError(procedureErrorMessage(e));
      if (isVersionConflict(e)) onConflict();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="run-reopen-dialog">
        <DialogHeader>
          <DialogTitle>Rouvrir la procédure ?</DialogTitle>
          <DialogDescription>L'équipe pourra de nouveau modifier les réponses. Le motif reste dans l'historique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="run-reopen-reason">Motif *</Label>
          <Textarea id="run-reopen-reason" rows={3} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="run-reopen-reason" />
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} className="bg-jamora hover:bg-jamora-hover text-white" data-testid="run-reopen-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Rouvrir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
